import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FadeUp } from "@/components/motion";
import { track } from "@/lib/analytics";
import { ARTICLES } from "@/data/insights";
import { ArrowUpRight, Linkedin, Mail, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINKEDIN_URL = "https://au.linkedin.com/in/barrettgeorge";

// Authority hub — structured to grow: videos and LinkedIn post embeds can be
// added here as additional cards/rows when the content exists.

function NewsletterCard() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      track("newsletter_subscribed");
      toast.success("You're subscribed. Practical positioning insights, straight to your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please check your email address and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#141414] p-6 md:p-8 flex flex-col" data-testid="newsletter-card">
      <span className="w-11 h-11 rounded-full bg-[#DFFF00] flex items-center justify-center mb-5">
        <Mail size={20} className="text-[#0A0A0A]" />
      </span>
      <h3 className="font-display text-xl font-bold tracking-tight text-[#F8F8F5]">The newsletter</h3>
      <p className="mt-2 font-body text-sm text-[#A1A1AA] leading-relaxed flex-1">
        Practical, evidence-led thinking on CVs, LinkedIn and professional positioning — sent occasionally, no noise.
      </p>
      <form onSubmit={subscribe} className="mt-5 flex flex-col sm:flex-row gap-2">
        <input
          data-testid="newsletter-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#27272A] px-4 py-3 font-body text-sm text-[#F8F8F5] placeholder:text-[#52525B] focus:outline-none focus:border-[#DFFF00] transition-colors"
        />
        <button
          data-testid="newsletter-subscribe-btn"
          type="submit"
          disabled={loading}
          className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-5 py-3 hover:bg-[#C8E600] transition-colors disabled:opacity-60 shrink-0"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Subscribe"}
        </button>
      </form>
    </div>
  );
}

export default function AuthorityHub() {
  const articles = ARTICLES.slice(0, 3);
  return (
    <section data-testid="authority-hub" id="content" className="relative bg-[#0A0A0A] py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Insights & Content</p>
            <h2 className="font-display font-bold tracking-tighter leading-[0.95] text-4xl md:text-6xl text-[#F8F8F5]">
              The thinking behind the <span className="font-serif-accent font-light">positioning.</span>
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="hub-all-insights-link"
            className="font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1.5 shrink-0"
          >
            View all insights <ArrowUpRight size={15} />
          </Link>
        </FadeUp>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {articles.map((a, i) => (
            <FadeUp key={a.slug} delay={i * 0.06} className="flex">
              <Link
                to={`/insights/${a.slug}`}
                data-testid={`hub-article-${a.slug}`}
                className="flex flex-col w-full border border-[#27272A] bg-[#141414] p-8 group hover:border-[#DFFF00] transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="tracking-label text-[9px] uppercase font-body font-bold text-[#DFFF00]">{a.category}</span>
                  <span className="font-body text-xs text-[#52525B]">{a.readingTime}</span>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#F8F8F5] leading-snug">{a.title}</h3>
                <p className="mt-3 font-body text-sm text-[#A1A1AA] leading-relaxed flex-1">{a.excerpt}</p>
                <span className="mt-5 font-body text-sm font-semibold text-[#F8F8F5] group-hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1">
                  Read <ArrowUpRight size={14} />
                </span>
              </Link>
            </FadeUp>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <FadeUp delay={0.1} className="flex">
            <NewsletterCard />
          </FadeUp>
          <FadeUp delay={0.15} className="flex">
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hub-linkedin-card"
              onClick={() => track("linkedin_click", { from: "authority-hub" })}
              className="flex flex-col w-full border border-[#27272A] bg-[#141414] p-8 group hover:border-[#DFFF00] transition-colors duration-300"
            >
              <span className="w-11 h-11 rounded-full bg-[#0A66C2] flex items-center justify-center mb-5">
                <Linkedin size={20} className="text-white" fill="currentColor" strokeWidth={0} />
              </span>
              <h3 className="font-display text-xl font-bold tracking-tight text-[#F8F8F5]">On LinkedIn</h3>
              <p className="mt-2 font-body text-sm text-[#A1A1AA] leading-relaxed flex-1">
                Follow George for regular thinking on positioning, CVs and how the Australian employment market actually reads your experience.
              </p>
              <span className="mt-5 font-body text-sm font-semibold text-[#F8F8F5] group-hover:text-[#DFFF00] transition-colors inline-flex items-center gap-1">
                Follow George Barrett <ArrowUpRight size={14} />
              </span>
            </a>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
